import type React from 'react';
import { Fragment } from 'react';
import Card from '@components/Card';
import ErrorText from '@components/ErrorText';
import IconMdi from '@components/IconMdi';
import { Show } from '@components/Show';
import { TooltipedIcon } from '@components/TooltipedIcon';
import type { UIChildrenMappingProps } from '@components/types';
import { useDatapointsStore } from '@ContractBuilder/store/datapoints.store';
import { mdiHelpCircleOutline, mdiMinus, mdiPlus } from '@mdi/js';
import { resolveSchema } from '@root/helpers/schema';
import type { JSONSchema, JSONSchemaProperties } from '@root/helpers/schema/types';
import mapChildren from '@src/mapChildren';
import { cloneDeep, cloneDeepWith, get, set } from 'lodash-es';

import Button from '../../Button';

export interface RepeaterProps extends UIChildrenMappingProps {
  className?: string;
  id: string;
  ownKey: string;
  item: JSONSchemaProperties;
}

const customCloneDeep = (value: any) => {
  return cloneDeepWith(value, (item) => {
    if (Array.isArray(item)) {
      return JSON.parse(JSON.stringify(item));
    }
    return undefined;
  });
};

const Repeater: React.FC<RepeaterProps> = ({
  autoGrow,
  className,
  id,
  item,
  ownKey,
  validationErrors,
  showQuestionKey,
  onChange,
  isReadOnly = false,
  isDisabled,
  hideErrors,
}) => {
  const { setFormValues, value } = useDatapointsStore(({ formValues, setFormValues }) => ({
    setFormValues,
    value: get(formValues, ownKey),
  }));

  const hasInitialValue = value && Array.isArray(value) && value.length > 0;
  const defaultValue = [];
  for (let i = 0; i < (item?.minItems ?? 0); i++) {
    defaultValue.push({});
  }
  const initialItems = hasInitialValue ? value : defaultValue;
  const canAdd = item?.maximum === undefined || initialItems?.length < item?.maximum;
  const total100Error = validationErrors?.find(
    (error) => error.instancePath === ownKey && error.keyword === 'total100',
  );

  const handleAdd = () => {
    const formValues = useDatapointsStore.getState().formValues;
    const cloned = customCloneDeep(formValues);
    set(cloned, ownKey, [...initialItems, {}]);
    setFormValues(cloned);
  };

  const handleRemove = () => {
    const formValues = useDatapointsStore.getState().formValues;
    const cloned = customCloneDeep(formValues);
    set(cloned, ownKey, initialItems.slice(0, initialItems.length - 1));
    setFormValues(cloned);
  };

  const schemaItem = item.items?.type === 'object' ? item.items : { properties: { [id]: item.items } };

  return (
    <div className={className}>
      <div className="flex items-center justify-between font-medium">
        <div className="flex justify-start gap-2">
          {item.shortName ?? item.title}
          {!!item?.helperText && (
            <TooltipedIcon path={mdiHelpCircleOutline} placement="top" content={item?.helperText} />
          )}
        </div>
      </div>
      {initialItems.map((_lineItem, index) => {
        const key = `${ownKey}[${index}]`;
        const clonedSchema = cloneDeep(schemaItem);
        set(
          clonedSchema,
          ['properties', item.key, 'title'],
          `${get(clonedSchema, ['properties', item.key, 'title'])} #${index + 1}`,
        );

        resolveSchema(clonedSchema, get(useDatapointsStore.getState().formValues, key));

        return (
          <Fragment key={key}>
            <Card className="mb-2" header={null}>
              <div className="flex justify-start justify-items-end gap-2">
                {mapChildren({
                  autoGrow,
                  onChange,
                  parentKey: key,
                  parentSchema: clonedSchema as JSONSchema,
                  validationErrors,
                  showQuestionKey,
                  isReadOnly,
                  isDisabled,
                  hideErrors,
                })}
              </div>
            </Card>
          </Fragment>
        );
      })}
      {total100Error && <ErrorText className="mb-4">{total100Error.message}</ErrorText>}
      <div className="flex">
        <Show when={!isReadOnly && (item?.minItems === undefined || item?.minItems < initialItems.length)}>
          <Button
            kind="ghost"
            className="mr-4 border-red-300 px-0 text-red-500 shadow-none focus:shadow-none enabled:hover:bg-white enabled:hover:text-red-600"
            onClick={handleRemove}
            isDisabled={isDisabled}
          >
            <IconMdi path={mdiMinus} />
            {`Remove ${item.title ?? 'a section'}`}
          </Button>
        </Show>
        <Show when={!isReadOnly && canAdd}>
          <Button
            onClick={handleAdd}
            kind="ghost"
            className="border-primary-300 text-primary-500 enabled:hover:text-primary-600 px-0 shadow-none focus:shadow-none enabled:hover:bg-white"
            isDisabled={isDisabled}
          >
            <IconMdi path={mdiPlus} />
            {`Add ${item.title ?? 'a section'}`}
          </Button>
        </Show>
      </div>
    </div>
  );
};

export default Repeater;
